import { collection, getDocs, query, where } from 'firebase/firestore'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { Deck } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button } from '../../components'
import { showToast } from '../../components/Toast'
import { deckFromSnapshot } from '../../lib/decks.js'
import { getDb } from '../../lib/firebase.js'
import { AdminApiError, moderateDeck } from '../../lib/admin-api.js'
import styles from './ModerationPage.module.css'

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

function formatDate(value: string): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : DATE_FORMATTER.format(date)
}

export default function ModerationPage(): JSX.Element {
  const { firebaseUser } = useAuth()
  const [decks, setDecks] = useState<Deck[]>([])
  const [loading, setLoading] = useState(true)
  const [unpublishTarget, setUnpublishTarget] = useState<Deck | null>(null)
  const [reason, setReason] = useState('')
  const [acting, setActing] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const loadFlaggedDecks = useCallback(async () => {
    setLoading(true)
    try {
      const snapshot = await getDocs(
        query(collection(getDb(), 'decks'), where('status', '==', 'flagged')),
      )
      setDecks(snapshot.docs.map((d) => deckFromSnapshot(d)))
    } catch {
      showToast('Could not load flagged decks.', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadFlaggedDecks()
  }, [loadFlaggedDecks])

  useEffect(() => {
    if (unpublishTarget) {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
      )
      firstFocusable?.focus()
    }
  }, [unpublishTarget])

  async function handleReinstate(deck: Deck) {
    setActing(true)
    try {
      const getIdToken = () => firebaseUser?.getIdToken() ?? Promise.resolve('')
      await moderateDeck(getIdToken, deck.id, { action: 'reinstate' })
      setDecks((prev) => prev.filter((d) => d.id !== deck.id))
      showToast('Deck reinstated.', 'success')
    } catch (err) {
      const msg = err instanceof AdminApiError ? err.message : 'Could not reinstate deck.'
      showToast(msg, 'error')
    } finally {
      setActing(false)
    }
  }

  async function handleConfirmUnpublish() {
    if (!unpublishTarget) return
    setActing(true)
    try {
      const getIdToken = () => firebaseUser?.getIdToken() ?? Promise.resolve('')
      const trimmed = reason.trim()
      await moderateDeck(getIdToken, unpublishTarget.id, {
        action: 'unpublish',
        ...(trimmed ? { reason: trimmed } : {}),
      })
      setDecks((prev) => prev.filter((d) => d.id !== unpublishTarget.id))
      showToast('Deck unpublished.', 'success')
      setUnpublishTarget(null)
      setReason('')
    } catch (err) {
      const msg = err instanceof AdminApiError ? err.message : 'Could not unpublish deck.'
      showToast(msg, 'error')
    } finally {
      setActing(false)
    }
  }

  return (
    <section className={styles.page} aria-labelledby="moderation-title">
      <div>
        <p className={styles.eyebrow}>Admin</p>
        <h2 id="moderation-title">Moderation</h2>
        <p className={styles.intro}>
          Review flagged decks. Unpublishing removes the deck from student view within 5 minutes.
          Reinstating clears all flags and returns the deck to published.
        </p>
      </div>

      {loading ? (
        <p role="status" aria-live="polite">
          Loading flagged decks…
        </p>
      ) : decks.length === 0 ? (
        <p className={styles.empty}>No flagged decks. All clear.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.deckTable}>
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Owner</th>
                <th scope="col">Flag count</th>
                <th scope="col">Last updated</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {decks.map((deck) => (
                <tr key={deck.id}>
                  <td>
                    <Link to={`/teacher/decks/${deck.id}`} className={styles.deckLink}>
                      {deck.title}
                    </Link>
                  </td>
                  <td>{deck.ownerId}</td>
                  <td>{String(deck.flaggedBy.length)}</td>
                  <td>{formatDate(deck.updatedAt)}</td>
                  <td>
                    <div className={styles.actions}>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        loading={acting}
                        onClick={() => {
                          setUnpublishTarget(deck)
                          setReason('')
                        }}
                      >
                        Unpublish
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={acting}
                        onClick={() => {
                          void handleReinstate(deck)
                        }}
                      >
                        Reinstate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unpublishTarget ? (
        <dialog
          open
          className={styles.dialog}
          aria-labelledby="unpublish-dialog-title"
          ref={dialogRef}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setUnpublishTarget(null)
              setReason('')
            }
          }}
        >
          <h3 id="unpublish-dialog-title" className={styles.dialogTitle}>
            Unpublish &ldquo;{unpublishTarget.title}&rdquo;?
          </h3>
          <p>
            This deck will be removed from student view. You can reinstate it later from the
            moderation queue.
          </p>
          <div className={styles.reasonField}>
            <label htmlFor="unpublish-reason" className={styles.reasonLabel}>
              Reason (optional)
            </label>
            <textarea
              id="unpublish-reason"
              className={styles.reasonInput}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
              }}
              maxLength={500}
              placeholder="Briefly explain why this deck is being unpublished."
            />
          </div>
          <div className={styles.dialogActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setUnpublishTarget(null)
                setReason('')
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={acting}
              onClick={() => {
                void handleConfirmUnpublish()
              }}
            >
              Unpublish
            </Button>
          </div>
        </dialog>
      ) : null}
    </section>
  )
}
